import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Services() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Appraisal Services - Virtus Realty Appraisal Services LLC';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Comprehensive property appraisal services in New Jersey including commercial, residential, estate valuations, tax appeals, litigation support, and market analysis.');
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'appraisal services, commercial appraisal, residential appraisal, estate valuation, tax appeal, litigation support, market analysis, New Jersey');
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Property Appraisal Services",
      "provider": {
        "@type": "ProfessionalService",
        "name": "Virtus Realty Appraisal Services LLC",
        "telephone": "(551) 206-2485",
        "email": "virtusappraisalsnj@gmail.com",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "NJ",
          "addressCountry": "US"
        }
      },
      "areaServed": {
        "@type": "State",
        "name": "New Jersey"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Appraisal Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Commercial Appraisals",
              "description": "Professional valuation services for office buildings, retail centers, industrial properties, and multi-family complexes."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Residential Appraisals",
              "description": "Accurate home valuations for single-family homes, condos, and townhouses."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Estate Valuations",
              "description": "Comprehensive property valuations for estate planning and settlement."
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Tax Appeals",
              "description": "Expert appraisal support for property tax appeals and litigation."
            }
          }
        ]
      }
    });
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const services = [
    {
      id: 'commercial',
      icon: 'ri-building-line',
      title: 'Commercial Appraisal Services',
      description: 'We provide detailed valuation reports for retail, office, multi-family, and mixed-use properties. Our certified appraisers deliver accurate market analyses for financing, development, acquisition, or investment decisions. Reports meet all USPAP and lender requirements.',
      features: ['Retail Properties', 'Office Buildings', 'Multi-Family Properties', 'Mixed-Use Developments', 'USPAP Compliant Reports', 'Investment Analysis'],
      image: 'https://public.readdy.ai/ai/img_res/f401d56d549d2ff8ad6b343062e2a380.jpg'
    },
    {
      id: 'residential',
      icon: 'ri-home-line',
      title: 'Residential Appraisal Services',
      description: 'Precise, compliant valuations for single-family homes, condos, multi-family dwellings, and new constructions. We work with banks, homeowners, and estate representatives to provide dependable appraisals for lending, refinancing, or sale purposes.',
      features: ['Single-Family Homes', 'Condominiums', 'Multi-Family Dwellings', 'New Construction', 'Refinancing Support', 'Estate Valuations'],
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20single-family%20residential%20home%20with%20well-maintained%20landscaping%2C%20traditional%20architecture%20with%20modern%20touches%2C%20suburban%20neighborhood%20setting%2C%20professional%20real%20estate%20photography%20with%20clear%20lighting&width=600&height=400&seq=residential-service&orientation=landscape'
    },
    {
      id: 'industrial',
      icon: 'ri-building-4-line',
      title: 'Industrial Property Appraisals',
      description: 'Specialized valuations for warehouses, manufacturing facilities, and distribution centers. We assess property utility, location, and market conditions to support lending, insurance, and asset management decisions.',
      features: ['Warehouses', 'Manufacturing Facilities', 'Distribution Centers', 'Utility Assessment', 'Location Analysis', 'Asset Management Support'],
      image: 'https://readdy.ai/api/search-image?query=Large%20industrial%20warehouse%20facility%20with%20loading%20docks%20and%20modern%20distribution%20infrastructure%2C%20clean%20industrial%20architecture%2C%20professional%20commercial%20photography%20with%20good%20lighting%20and%20clear%20details&width=600&height=400&seq=industrial-service&orientation=landscape'
    },
    {
      id: 'estate',
      icon: 'ri-file-text-line',
      title: 'Estate & Trust Valuations',
      description: 'Professional appraisals to assist attorneys, accountants, and executors in estate planning or settlement. Our objective valuations ensure accurate reporting for IRS, probate, and tax purposes.',
      features: ['Estate Planning', 'Probate Valuations', 'IRS Reporting', 'Trust Administration', 'Tax Compliance', 'Legal Documentation'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20legal%20documents%20and%20property%20valuation%20reports%20on%20elegant%20desk%20with%20classical%20architectural%20elements%2C%20formal%20business%20setting%20with%20gold%20accents%2C%20sophisticated%20office%20environment&width=600&height=400&seq=estate-service&orientation=landscape'
    },
    {
      id: 'divorce',
      icon: 'ri-home-heart-line',
      title: 'Divorce Appraisals',
      description: 'Divorce proceedings often require a clear, unbiased opinion of a property\'s value. Virtus Realty Appraisal Services LLC provides independent real estate appraisals to support equitable distribution, buyouts, and settlement negotiations. We offer fair, well-documented reports that are suitable for attorneys, mediators, and the court, and we approach every assignment with discretion and professionalism.',
      features: ['Neutral, Third-Party Valuations', 'Support for Buyouts and Settlements', 'Reports Suitable for Legal and Mediation Use', 'Residential and Income-Producing Properties'],
      image: 'https://static.readdy.ai/image/0c5a07476bd7a7109b05d9670ab2ad74/340be9de904bd042c9a4f0d825c6c047.jpeg',
      customIcon: true
    },
    {
      id: 'tax-litigation',
      icon: 'ri-scales-line',
      title: 'Tax Appeal & Litigation Support',
      description: 'Expert support for property tax appeals and legal proceedings. We prepare defensible valuation analyses and provide expert witness testimony when necessary.',
      features: ['Property Tax Appeals', 'Expert Witness Testimony', 'Legal Proceedings', 'Defensible Analysis', 'Court Documentation', 'Litigation Support'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20courtroom%20or%20legal%20office%20setting%20with%20classical%20columns%20and%20formal%20architecture%2C%20legal%20documents%20and%20scales%20of%20justice%2C%20sophisticated%20professional%20environment%20with%20gold%20and%20black%20accents&width=600&height=400&seq=litigation-service&orientation=landscape'
    },
    {
      id: 'bank-mortgage',
      icon: 'ri-bank-line',
      title: 'Bank & Mortgage Financing Appraisals',
      description: 'Trusted by lenders for accurate property valuations that meet federal and banking compliance standards. Rapid turnaround and secure electronic delivery available.',
      features: ['Federal Compliance', 'Banking Standards', 'Rapid Turnaround', 'Secure Delivery', 'Lender Requirements', 'Electronic Reports'],
      image: 'https://readdy.ai/api/search-image?query=Modern%20bank%20building%20with%20classical%20architectural%20elements%2C%20professional%20financial%20institution%20facade%20with%20columns%20and%20elegant%20design%2C%20business%20district%20setting%20with%20sophisticated%20appearance&width=600&height=400&seq=banking-service&orientation=landscape'
    },
    {
      id: 'market-rent',
      icon: 'ri-line-chart-line',
      title: 'Market Rent Studies & Feasibility Analysis',
      description: 'Comprehensive market studies to help investors and developers evaluate property performance, rental trends, and project feasibility.',
      features: ['Market Analysis', 'Rental Trends', 'Project Feasibility', 'Investment Evaluation', 'Performance Studies', 'Developer Support'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20market%20analysis%20charts%20and%20graphs%20on%20elegant%20desk%20with%20architectural%20blueprints%2C%20modern%20office%20setting%20with%20gold%20accents%2C%20business%20analytics%20and%20real%20estate%20development%20planning&width=600&height=400&seq=market-service&orientation=landscape'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'We discuss your specific appraisal needs and timeline requirements.'
    },
    {
      step: '02',
      title: 'Property Inspection',
      description: 'Thorough on-site evaluation and documentation of the property.'
    },
    {
      step: '03',
      title: 'Market Analysis',
      description: 'Comprehensive research and analysis of comparable properties and market conditions.'
    },
    {
      step: '04',
      title: 'Report Delivery',
      description: 'Professional, compliant valuation report delivered within agreed timeframe.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url('https://readdy.ai/api/search-image?query=Classical%20Roman-inspired%20architecture%20with%20elegant%20columns%20and%20gold%20accents%2C%20professional%20business%20building%20facade%2C%20sophisticated%20design%20with%20black%20and%20gold%20color%20scheme%2C%20architectural%20photography&width=1920&height=600&seq=services-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight font-elegant">
            Professional <span className="text-gradient-gold">Appraisal Services</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-4xl mx-auto leading-relaxed font-modern">
            Certified General Appraisers delivering precise, compliant, and timely valuation reports for all property types
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="gradient-gold text-black-900 hover:shadow-gold font-semibold">
                <i className="ri-file-text-line mr-2"></i>
                Request an Appraisal
              </Button>
            </Link>
            <div className="text-center">
              <a href="tel:+15512062485">
                <button className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap gradient-gold text-black-900 hover:shadow-gold px-8 py-4 text-lg gradient-gold text-black-900 hover:shadow-gold font-semibold">
                  <i className="ri-phone-line mr-2"></i>
                  Call (551) 206-2485
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-4 font-elegant">
              Comprehensive Valuation Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-modern">
              Our certified team provides expert appraisal services across all property types, ensuring accuracy and compliance with industry standards
            </p>
          </div>
          
          <div className="space-y-16">
            {services.map((service, index) => (
              <div 
                key={index} 
                id={service.id} 
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center scroll-mt-40 ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-16 h-16 gradient-gold rounded-xl flex items-center justify-center mb-6">
                    {service.customIcon ? (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <i className="ri-home-line text-black-900 text-2xl absolute"></i>
                        <i className="ri-scales-2-line text-black-900 text-xl absolute" style={{ transform: 'rotate(-15deg) translateX(8px)' }}></i>
                      </div>
                    ) : (
                      <i className={`${service.icon} text-black-900 text-2xl`}></i>
                    )}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-black-900 mb-4 font-elegant">
                    {service.title}
                  </h3>
                  <p className="text-lg text-gray-600 mb-6 font-modern leading-relaxed">
                    {service.description}
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-700 font-modern">
                        <i className="ri-check-line text-gold-500 mr-2 flex-shrink-0"></i>
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Link to="/contact">
                    <Button className="gradient-gold text-black-900 hover:shadow-gold font-semibold">
                      Get Quote for This Service
                      <i className="ri-arrow-right-line ml-2"></i>
                    </Button>
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="rounded-xl shadow-lg object-cover w-full h-80"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-4 font-elegant">
              Our Appraisal Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-modern">
              A systematic approach ensuring accuracy, compliance, and timely delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 gradient-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-black-900 font-elegant">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-black-900 mb-3 font-elegant">
                  {step.title}
                </h3>
                <p className="text-gray-600 font-modern">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-6 font-elegant">
                Certified General Appraisers
              </h2>
              <p className="text-lg text-gray-600 mb-6 font-modern leading-relaxed">
                Our team holds the highest level of real estate appraisal certification, enabling us to appraise all types of real property without regard to value or complexity. We maintain continuing education and adhere to the Uniform Standards of Professional Appraisal Practice (USPAP).
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <i className="ri-shield-check-line text-gold-500 text-xl"></i>
                  <span className="text-gray-700 font-modern">USPAP Compliant Reports</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-award-line text-gold-500 text-xl"></i>
                  <span className="text-gray-700 font-modern">Certified General Appraiser License</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-award-line text-gold-500 text-xl"></i>
                  <span className="text-gray-700 font-modern">Experienced in Complex & High-Value Appraisals</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-file-shield-line text-gold-500 text-xl"></i>
                  <span className="text-gray-700 font-modern">Professional Liability Insurance</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/0c5a07476bd7a7109b05d9670ab2ad74/5f1038eef24f673c58de364c993e7579.png"
                alt="Certified General Appraiser credentials and professional tools"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-elegant">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto font-modern leading-relaxed">
            At Virtus Realty Appraisal Services LLC, accuracy, integrity, and professionalism are the foundation of every valuation we deliver.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="gradient-gold text-black-900 hover:shadow-gold font-semibold">
                <i className="ri-file-text-line mr-2"></i>
                Request an Appraisal
              </Button>
            </Link>
            <Link to="/about">
              <Button size="lg" variant="outline" className="border-gold-400 text-gold-400 hover:bg-gold-400 hover:text-black-900 font-semibold">
                <i className="ri-team-line mr-2"></i>
                Learn About Our Team
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
